
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  FileText, 
  Eye, 
  MessageCircle, 
  User, 
  Upload,
  MoreHorizontal,
  Heart,
  Share2,
  Calendar,
  TrendingUp
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const weeklyData = [
  { name: 'Mon', views: 1200, likes: 450, comments: 89 },
  { name: 'Tue', views: 1900, likes: 620, comments: 142 },
  { name: 'Wed', views: 1600, likes: 580, comments: 98 },
  { name: 'Thu', views: 2200, likes: 780, comments: 165 },
  { name: 'Fri', views: 2800, likes: 920, comments: 203 },
  { name: 'Sat', views: 3200, likes: 1100, comments: 245 },
  { name: 'Sun', views: 2600, likes: 890, comments: 187 },
];

const demographicsData = [
  { name: '18-24', value: 30, color: '#ff6b6b' },
  { name: '25-34', value: 35, color: '#4ecdc4' },
  { name: '35-44', value: 20, color: '#45b7d1' },
  { name: '45+', value: 15, color: '#96ceb4' },
];

const recentPosts = [
  {
    id: 1,
    content: "Just launched our new product line! What do you think? 🚀",
    timestamp: "2 hours ago",
    likes: 245,
    comments: 18,
    shares: 12,
    views: 1247
  },
  {
    id: 2,
    content: "Behind the scenes of our latest photoshoot 📸",
    timestamp: "1 day ago",
    likes: 189,
    comments: 24,
    shares: 8,
    views: 892
  },
  {
    id: 3,
    content: "Thank you for 10K followers! You're amazing! ❤️",
    timestamp: "3 days ago",
    likes: 567,
    comments: 89,
    shares: 34,
    views: 2156
  }
];

export const PlatformDetails = ({ platform }) => {
  const actionItems = [
    { icon: FileText, label: 'Post Content', description: 'Create and schedule new posts' },
    { icon: Eye, label: 'View Insights', description: 'Analyze performance metrics' },
    { icon: MessageCircle, label: 'Manage Comments', description: 'Respond to comments and messages' },
    { icon: User, label: 'Read Profile', description: 'View and edit profile information' },
    { icon: Upload, label: 'Upload Media', description: 'Add photos and videos to library' },
  ];

  return (
    <div className="space-y-6">
      {/* Platform Header */}
      <Card className="shadow-lg">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className={`w-16 h-16 rounded-full ${platform.color} flex items-center justify-center`}>
                {platform.icon && <platform.icon className="w-8 h-8 text-white" />}
              </div>
              <div>
                <h2 className="text-2xl font-bold">{platform.name} Dashboard</h2>
                <Badge variant="secondary" className="mt-1">
                  Account Connected
                </Badge>
              </div>
            </div>
            <Button className="gap-2">
              <Calendar className="w-4 h-4" />
              Schedule Post
            </Button>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-2xl font-bold text-slate-900">28.7K</p>
              <p className="text-sm text-slate-600">Followers</p>
              <p className="text-xs text-green-600">+18.3%</p>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-2xl font-bold text-slate-900">156</p>
              <p className="text-sm text-slate-600">Posts</p>
              <p className="text-xs text-blue-600">This month</p>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-2xl font-bold text-slate-900">12.4%</p>
              <p className="text-sm text-slate-600">Engagement</p>
              <p className="text-xs text-green-600">+2.1%</p>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-2xl font-bold text-slate-900">89.2K</p>
              <p className="text-sm text-slate-600">Reach</p>
              <p className="text-xs text-green-600">+15.7%</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {actionItems.map((item, index) => (
          <Card key={index} className="hover:shadow-lg transition-shadow duration-300 cursor-pointer group">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 mx-auto mb-3 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full flex items-center justify-center group-hover:from-purple-200 group-hover:to-pink-200 transition-colors">
                <item.icon className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-semibold text-sm mb-1">{item.label}</h3>
              <p className="text-xs text-slate-600">{item.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Analytics Tabs */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-4 bg-white border">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="posts">Recent Posts</TabsTrigger>
          <TabsTrigger value="audience">Audience</TabsTrigger>
          <TabsTrigger value="growth">Growth</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Weekly Performance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={weeklyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="views" stroke="#8884d8" strokeWidth={2} />
                    <Line type="monotone" dataKey="likes" stroke="#ff6b6b" strokeWidth={2} />
                    <Line type="monotone" dataKey="comments" stroke="#4ecdc4" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle>Top Performing Content</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {recentPosts.slice(0, 3).map((post) => (
                  <div key={post.id} className="p-4 border rounded-lg">
                    <p className="text-sm mb-2">{post.content}</p>
                    <div className="flex items-center justify-between text-xs text-slate-600">
                      <span>{post.timestamp}</span>
                      <div className="flex gap-4">
                        <span>👁 {post.views}</span>
                        <span>❤️ {post.likes}</span>
                        <span>💬 {post.comments}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="posts" className="space-y-6">
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle>Recent Posts</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {recentPosts.map((post) => (
                <div key={post.id} className="p-4 border rounded-lg hover:bg-slate-50">
                  <div className="flex justify-between items-start mb-3">
                    <p className="text-sm flex-1">{post.content}</p>
                    <Button variant="ghost" size="sm">
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-600">{post.timestamp}</span>
                    <div className="flex gap-6 text-sm">
                      <div className="flex items-center gap-1">
                        <Eye className="w-4 h-4" />
                        {post.views}
                      </div>
                      <div className="flex items-center gap-1">
                        <Heart className="w-4 h-4" />
                        {post.likes}
                      </div>
                      <div className="flex items-center gap-1">
                        <MessageCircle className="w-4 h-4" />
                        {post.comments}
                      </div>
                      <div className="flex items-center gap-1">
                        <Share2 className="w-4 h-4" />
                        {post.shares}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="audience" className="space-y-6">
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle>Audience Demographics</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={demographicsData}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {demographicsData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="growth" className="space-y-6">
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle>Growth Metrics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-6 bg-green-50 rounded-lg">
                  <p className="text-3xl font-bold text-green-600">+18.3%</p>
                  <p className="text-sm text-slate-600">Follower Growth</p>
                  <p className="text-xs text-slate-500">vs last month</p>
                </div>
                <div className="text-center p-6 bg-blue-50 rounded-lg">
                  <p className="text-3xl font-bold text-blue-600">+24.7%</p>
                  <p className="text-sm text-slate-600">Engagement Growth</p>
                  <p className="text-xs text-slate-500">vs last month</p>
                </div>
                <div className="text-center p-6 bg-purple-50 rounded-lg">
                  <p className="text-3xl font-bold text-purple-600">+31.2%</p>
                  <p className="text-sm text-slate-600">Reach Growth</p>
                  <p className="text-xs text-slate-500">vs last month</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
